import type { ReactNode } from 'react';
import { useState, useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { DefaultWebViewOptions, InAppBrowser } from '@capacitor/inappbrowser';
import type { PlainRank } from 'api/generated';
import Analytics from 'ui/design-base/Analytics';
import { isCrawler, isLoggedIn } from './authentication';
import { pushEvent, pushEventWithProperties } from './crmUtils';
import { mapRankValue, mapValueRank } from './ranks';

type ConditionalWrapperProps = {
  condition: boolean;
  wrapper: (children: ReactNode) => ReactNode;
  children: ReactNode;
};

export const ConditionalWrapper = ({ condition, wrapper, children }: ConditionalWrapperProps) =>
  condition ? wrapper(children) : children;

type ContainerWrapperProps = {
  children: ReactNode;
};

export const ContainerWrapper = ({ children }: ContainerWrapperProps) => (
  <div className='external-container'>
    <div className='internal-container'>{children}</div>
  </div>
);

export const openInExternalUrl = async (url: string | null) => {
  if (url) {
    if (url.includes('magazine')) {
      pushEventWithProperties('TT - LINK - open magazine external url', {
        url,
        source: Capacitor.isNativePlatform() ? 'app' : 'web',
      });
    }
    if (Capacitor.isNativePlatform()) {
      await InAppBrowser.openInWebView({
        url: `${url + (url.includes('?') ? '&' : '?')}utm_source=tennistalker_app&utm_medium=referral`,
        options: DefaultWebViewOptions,
      });
    } else {
      window
        .open(`${url + (url.includes('?') ? '&' : '?')}utm_source=tennistalker_web&utm_medium=referral`, '_blank')
        ?.focus();
    }
  }
};

// Hook
const cachedScripts: string[] = [];
export const useScript = (src: string | undefined) => {
  // Keeping track of script loaded and error state
  const [state, setState] = useState({
    loaded: false,
    error: false,
  });

  useEffect(
    () => {
      // If script src is not provided, set loaded to true and return
      if (!src) {
        setState({
          loaded: true,
          error: false,
        });
        return;
      }
      // If cachedScripts array already includes src that means another instance ...
      // ... of this hook already loaded this script, so no need to load again.
      if (cachedScripts.includes(src)) {
        setState({
          loaded: true,
          error: false,
        });
      } else {
        cachedScripts.push(src);

        // Create script
        const script = document.createElement('script');
        script.src = src;
        script.async = true;

        // Script event listener callbacks for load and error
        const onScriptLoad = () => {
          setState({
            loaded: true,
            error: false,
          });
        };

        const onScriptError = () => {
          // Remove from cachedScripts we can try loading again
          const index = cachedScripts.indexOf(src);
          if (index >= 0) cachedScripts.splice(index, 1);
          script.remove();

          setState({
            loaded: true,
            error: true,
          });
        };

        script.addEventListener('load', onScriptLoad);
        script.addEventListener('error', onScriptError);

        // Add script to document body
        document.body.appendChild(script);

        // Remove event listeners on cleanup
        return () => {
          script.removeEventListener('load', onScriptLoad);
          script.removeEventListener('error', onScriptError);
          // Also remove the script element itself to prevent duplicates if the component unmounts and remounts before the script is fully loaded.
          if (document.body.contains(script)) {
            document.body.removeChild(script);
          }
        };
      }
    },
    [src], // Only re-run effect if script src changes
  );

  return [state.loaded, state.error];
};

export const getClevertapWebInitConditions = () => {
  if (Capacitor.isNativePlatform()) {
    return false;
  }
  if (isCrawler()) {
    return false;
  }
  if (!isLoggedIn()) {
    return false;
  }
  // if (window.location.href.includes('localhost')) {
  //   return false;
  // }
  return true;
};

export const delay = (timer: number) =>
  new Promise<void>((resolve) => {
    timer = timer || 100;
    setTimeout(() => {
      resolve();
    }, timer);
  });

export const reducer = (state: any, action: { type: string; payload: any }) => {
  const { type, payload } = action;
  return { ...state, [type]: payload };
};

const testWebP: () => Promise<boolean> = () =>
  new Promise((res) => {
    const webP = new Image();
    webP.src =
      'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
    webP.onload = webP.onerror = () => {
      res(webP.height === 2);
    };
  });

let isSupported: boolean;
export const isWebPSupported: any = async () => {
  if (isSupported === undefined) {
    isSupported = await testWebP();
  }
  return isSupported;
};

export const isPadel = (): boolean => import.meta.env.VITE_APPLICATION_TYPE === 'padel';

export const encodeToUrlQueryParameters = (params: Record<string, string | number | boolean | undefined | null>) =>
  Object.keys(params)
    .filter((key) => params[key])
    .sort()
    .map((key) => `${encodeURIComponent(key)}=${params[key] ? encodeURIComponent(params[key]) : ''}`)
    .join('&');

export const openLandingPageMembership = async () => {
  await Analytics.sendEvent({
    category: 'TT Club',
    action: 'Click Landing TT Club',
    label: 'Button TT Club',
  });
  pushEvent('TT - LINK - open membership landing page');
  const url = 'https://magazine.tennistalker.it/jointtclub/';
  openInExternalUrl(url);
};

export const openPaymentPageMembership = async () => {
  await Analytics.sendEvent({
    category: 'TT Club',
    action: 'Click Payment TT Club',
    label: 'Button Buy TT Club',
  });
  pushEvent('TT - LINK - open membership shopify chart');
  const url =
    'https://shop.tennistalker.it/tools/recurring/checkout_link?magic=eyJpdGVtcyI6IFt7ImlkIjogNDE5MjE1MDkzNTk3ODAsICJxdWFudGl0eSI6IDEsICJzZWxsaW5nX3BsYW4iOiA2ODk2NTIyNjkzOTAsICJzZWxsaW5nX3BsYW5fZ3JvdXBfaWQiOiAzMTUwOTcyNTJ9XX0=&store_id=163085';
  window.open(url, '_blank')?.focus();
};

export const openMagazine = async () => {
  await Analytics.sendEvent({
    category: 'TT Magazine',
    action: 'Click Magazine',
    label: 'Button Magazine',
  });
  pushEvent('TT - LINK - open magazine homepage');
  if (Capacitor.isNativePlatform()) {
    window.open('https://magazine.tennistalker.it?utm_source=tennistalker_app&utm_medium=referral', '_blank')?.focus();
  } else {
    window.open('https://magazine.tennistalker.it?utm_source=tennistalker_web&utm_medium=referral', '_blank')?.focus();
  }
};

export const openPostSellPageMembership = async () => {
  await Analytics.sendEvent({
    category: 'TT Club',
    action: 'Click Post Sell TT Club',
    label: 'Button Post Sell TT Club',
  });
  pushEvent('TT - LINK - open post sell page');
  const url = 'https://magazine.tennistalker.it/ttclubwelcome/';
  openInExternalUrl(url);
};

export const openMembershipSubscriptionManagementPage = async () => {
  await Analytics.sendEvent({
    category: 'TT Club',
    action: 'Click Subscription Management TT Club',
    label: 'Button Subscription Management TT Club',
  });
  pushEvent('TT - LINK - open subscription management page');
  const url = 'https://shop.tennistalker.it/tools/recurring/get-subscription-access';
  openInExternalUrl(url);
};

export const getPreviousRank: (rank: PlainRank | undefined) => PlainRank | '-' = (rank) => {
  if (!rank) return '-';
  const rankValue = mapRankValue[rank];
  if (rankValue > 1) return mapValueRank[rankValue - 1] as PlainRank;
  return rank;
};

export const getNextRank: (rank: PlainRank | undefined) => PlainRank | '-' = (rank) => {
  if (!rank) return '-';
  const rankValue = mapRankValue[rank];
  if (rankValue < 40) return mapValueRank[rankValue + 1] as PlainRank;
  return rank;
};

export const getTweCode: (date: Date) => string = (date) => {
  const month = date.getMonth() + 1;
  if (month === 1) return 'TWEVIP27HBH';
  if (month === 2) return 'TWEVIP54HSI';
  if (month === 3) return 'TWEVIP89KFD';
  if (month === 4) return 'TWEVIP74HXE';
  if (month === 5) return 'TWEVIP9IG45';
  if (month === 6) return 'TWEVIP0I23H';
  if (month === 7) return 'TWEVIP5G12J';
  if (month === 8) return 'TWEPIEDKEX9';
  if (month === 9) return 'TWEVIP8HDJ3';
  if (month === 10) return 'TWEVIP8FB2P';
  if (month === 11) return 'TWEVIP25POI';
  if (month === 12) return 'TWEVIP46OHT';
  return '';
};

export enum MatchFilterTypes {
  ALL = 'ALL',
  WINS = 'WINS',
  LOSSES = 'LOSSES',
  DOUBLES = 'DOUBLES',
  SINGLE_WINS = 'SINGLE_WINS',
  SINGLE_LOSSES = 'SINGLE_LOSSES',
  NAME = 'NAME',
  IS_IN_BEST_MATCHES = 'IS_IN_BEST_MATCHES',
}

export const italianDateToJSDate = (dateString?: string | null) => {
  if (!dateString) return new Date();
  const [day, month, year] = dateString.split('/').map(Number); // Split and convert to numbers
  return new Date(year, month - 1, day); // Month is 0-indexed in JavaScript
};

export const racketBrands = [
  { id: 0, name: 'Babolat' },
  { id: 1, name: 'Wilson' },
  { id: 2, name: 'Head' },
  { id: 3, name: 'Prince' },
  { id: 4, name: 'Yonex' },
  { id: 5, name: 'Dunlop' },
  { id: 6, name: 'Pacific' },
  { id: 7, name: 'ProKennex' },
  { id: 8, name: 'Tecnifibre' },
  { id: 9, name: 'Volkl' },
  { id: 10, name: 'Gamma' },
  { id: 11, name: 'Snauwaert' },
  { id: 12, name: 'Angell' },
  { id: 13, name: 'Diadem' },
  { id: 14, name: 'Fischer' },
  { id: 15, name: 'Gosen' },
  { id: 16, name: 'Mantis' },
  { id: 17, name: 'Pacific' },
  { id: 18, name: 'Slazenger' },
  { id: 19, name: 'Lacoste' },
  { id: 99, name: 'Altro' },
];

export const shoesBrands = [
  { id: 0, name: 'Joma' },
  { id: 1, name: 'Asics' },
  { id: 2, name: 'Adidas' },
  { id: 3, name: 'Nike' },
  { id: 4, name: 'Babolat' },
  { id: 5, name: 'Head' },
  { id: 6, name: 'K-Swiss' },
  { id: 7, name: 'Lotto' },
  { id: 8, name: 'Mizuno' },
  { id: 9, name: 'Yonex' },
  { id: 10, name: 'Diadora' },
  { id: 11, name: 'New Balance' },
  { id: 12, name: 'Wilson' },
  { id: 13, name: 'Decathlon' },
  { id: 14, name: 'ON' },
  { id: 15, name: 'Lacoste' },
  { id: 99, name: 'Altro' },
];

export const clothingBrands = [
  { id: 21, name: 'Old School' },
  { id: 22, name: 'Yoxoi' },
  { id: 20, name: 'Double U' },
  { id: 0, name: 'Joma' },
  { id: 1, name: 'Asics' },
  { id: 2, name: 'Adidas' },
  { id: 3, name: 'Nike' },
  { id: 4, name: 'Babolat' },
  { id: 5, name: 'Head' },
  { id: 6, name: 'K-Swiss' },
  { id: 7, name: 'Lotto' },
  { id: 8, name: 'Mizuno' },
  { id: 9, name: 'Yonex' },
  { id: 10, name: 'Diadora' },
  { id: 11, name: 'New Balance' },
  { id: 12, name: 'Wilson' },
  { id: 13, name: 'Australian' },
  { id: 14, name: 'Ellesse' },
  { id: 15, name: 'Lacoste' },
  { id: 16, name: 'Hydrogen' },
  { id: 17, name: 'Uniqlo' },
  { id: 18, name: 'Sergio Tacchini' },
  { id: 19, name: 'Under Armour' },
  { id: 24, name: 'Emporio Armani' },
  { id: 25, name: 'Fila' },
  { id: 26, name: 'Le Coq Sportif' },
  { id: 23, name: 'ON' },
  { id: 99, name: 'Altro' },
];

// A map defining the English key and its Italian translation.
export const surfaceTranslations = {
  clay_court: 'Terra rossa',
  synthetic: 'Sintetico',
  grass_court: 'Erba naturale',
  synthetic_grass: 'Erba sintetica',
  hard_court: 'Cemento',
  synthetic_clay: 'Terra sintetica',
  other: 'Altro',
  unknown: 'Sconosciuta',
};

export const getSurfaceType = (englishName: keyof typeof surfaceTranslations | string): string => {
  if (englishName in surfaceTranslations) {
    return surfaceTranslations[englishName as keyof typeof surfaceTranslations];
  }
  return englishName;
};

export const getMaxDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 14);
  return date;
};

export const getBrandName = (type: string, id: number | string) => {
  if (typeof id === 'string') {
    id = parseInt(id);
  }
  if (type === 'racket') {
    const res = racketBrands.find((r) => r.id === id);
    return res && res.name;
  } else if (type === 'shoes') {
    const res = shoesBrands.find((r) => r.id === id);
    return res && res.name;
  } else {
    const res = clothingBrands.find((r) => r.id === id);
    return res && res.name;
  }
};

export const isNumeric = (value: any) => /^-?\d+$/.test(value);

const IOS_APP_ID = '1553063249';
const ANDROID_PACKAGE_NAME = 'it.tennistalker.app';
export const IOS_APP_URL = `https://apps.apple.com/it/app/id${IOS_APP_ID}`;
export const ANDROID_APP_URL = `https://play.google.com/store/apps/details?id=${ANDROID_PACKAGE_NAME}`;

export const getShareBaseUrl = (): string => {
  if (!Capacitor.isNativePlatform()) {
    return window.location.origin || 'https://www.tennistalker.it';
  }

  switch (import.meta.env.VITE_ENV) {
    case 'production':
      return import.meta.env.VITE_FRONTEND_URL_PROD || 'https://www.tennistalker.it';
    case 'staging':
      return import.meta.env.VITE_FRONTEND_URL_STAGING || 'https://www.tennistalker.it';
    default:
      return import.meta.env.VITE_FRONTEND_URL || 'https://www.tennistalker.it';
  }
};
